import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AlertTriangle, Inbox, type LucideIcon } from "lucide-react";

/** Consistent empty state — dashed card + faint icon + one line. Used everywhere a list,
 *  section, or panel has nothing to show. */
export function EmptyState({
  icon: Icon = Inbox,
  children,
  className,
}: {
  icon?: LucideIcon;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border border-dashed bg-card p-8 text-center", className)}>
      <Icon className="mx-auto mb-2 size-5 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

/** Consistent error state, distinct from "empty" — offers a retry. */
export function ErrorState({
  onRetry,
  children,
  className,
}: {
  onRetry?: () => void;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border bg-card p-8 text-center", className)}>
      <p className="text-sm text-muted-foreground">{children ?? "Couldn't load this. Please try again."}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}

/** Pulsing placeholder block for loading skeletons. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-md bg-accent", className)} />;
}

/** Slim inline error banner for failed mutations — the admin surfaces have no toast system, so a
 *  write that fails silently is invisible. Renders nothing until `error` is set; pulls the API
 *  message when available. Sits next to the control it belongs to. */
export function InlineError({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const message = error instanceof ApiError ? error.message : "Something went wrong. Please try again.";
  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2 rounded-md border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]",
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span className="min-w-0">{message}</span>
    </div>
  );
}
